import type {
  BacktestAnalysis,
  BacktestResult,
} from "./types";
import type { MarketDataHistoryLimit } from "./market-data-error.ts";

export interface AnalysisJobError {
  reason?: string;
  code?: string;
  localization_key?: string;
  arguments?: Record<string, string | number | boolean | null>;
  localized_message?: string;
  message?: string;
  retryable?: boolean;
  retry_after_seconds?: number;
}

export interface AnalysisJob {
  id: string;
  status: string;
  stage?: string;
  progress?: number;
  created_at?: string;
  updated_at?: string;
  expires_at?: string;
  poll_url?: string;
  result?: BacktestAnalysis;
  error?: AnalysisJobError;
}

export interface AnalysisJobEnvelope {
  job: AnalysisJob;
}

export interface AnalysisCapabilities {
  trading_methods?: { enabled?: boolean; schema_versions?: number[]; method_versions?: Record<string, number[]>; catalog_url?: string };
  resumable_analysis_jobs?: boolean;
  analysis_jobs_endpoint?: string;
  derivatives_backtesting?: {
    enabled?: boolean;
    schema_version?: number;
    execution_models?: string[];
    margin_modes?: string[];
    max_leverage?: number;
    funding_supported?: boolean;
    liquidation_supported?: boolean;
  };
  shareable_results_v1?: {
    enabled?: boolean;
    reproducible_strategies?: boolean;
    modes?: string[];
  };
  strategy_community_v1?: {
    enabled?: boolean;
    schema_versions?: number[];
    feed_url?: string;
    detail_url_template?: string;
    owner_publications_url?: string;
    favorites_url?: string;
    publish_url?: string;
    publishable_sources_url?: string;
    favorite_url_template?: string;
    archive_url_template?: string;
    duplicate_attribution_url_template?: string;
    report_url_template?: string;
    block_creator_url_template?: string;
    blocked_creators_url?: string;
    default_sort?: string;
    live_execution?: boolean;
    creator_payouts?: boolean;
    performance_verified?: boolean;
    publishing_requires_review?: boolean;
    creator_blocking?: boolean;
  };
}

export interface ServiceCapabilities {
  capabilities?: AnalysisCapabilities;
}

export type BacktestAnalysisStart =
  | { kind: "completed"; analysis: BacktestAnalysis }
  | { kind: "pending"; job: AnalysisJob };

export interface AnalysisActionInput {
  originalPrompt: string;
  preferredLanguage: string;
  result: BacktestResult;
}

export interface PendingAnalysisAction {
  version: 1;
  accountID: string;
  idempotencyKey: string;
  inputFingerprint: string;
  createdAt: string;
  input: AnalysisActionInput;
  job?: AnalysisJob;
}

export interface PendingLockedHoldoutAction {
  version: 1;
  accountID: string;
  trialID: string;
  createdAt: string;
  result: BacktestResult;
  analysis: BacktestAnalysis;
}

const ANALYSIS_ACTION_PREFIX = "backtest.web.analysis-action.v1";
const LOCKED_HOLDOUT_PREFIX = "backtest.web.locked-holdout.v1";
const MAX_SESSION_AGE_MS = 36 * 60 * 60 * 1_000;
const MAX_INLINE_SESSION_BYTES = 256_000;

export interface AsyncWorkflowStorage {
  get(key: string): Promise<unknown>;
  set(key: string, value: unknown): Promise<void>;
  remove(key: string): Promise<void>;
}

interface DurableSessionMarker {
  version: 1;
  accountID: string;
  createdAt: string;
  durable: true;
  kind: "analysis" | "locked_holdout";
  resumeAction?: PendingAnalysisAction | PendingLockedHoldoutAction;
}

function scopedKey(prefix: string, accountID: string): string {
  return `${prefix}.${encodeURIComponent(accountID)}`;
}

export function analysisActionStorageKey(accountID: string): string {
  return scopedKey(ANALYSIS_ACTION_PREFIX, accountID);
}

export function lockedHoldoutStorageKey(accountID: string): string {
  return scopedKey(LOCKED_HOLDOUT_PREFIX, accountID);
}

export function createAnalysisIdempotencyKey(
  randomUUID: () => string = () => crypto.randomUUID(),
): string {
  return `web-analysis-v2-${randomUUID().toLowerCase()}`;
}

export async function analysisInputFingerprint(
  input: AnalysisActionInput,
  digest: (
    algorithm: AlgorithmIdentifier,
    data: BufferSource,
  ) => Promise<ArrayBuffer> = (algorithm, data) =>
    crypto.subtle.digest(algorithm, data),
): Promise<string> {
  const payload = stableJSONString({
    contractVersion: "web-analysis-input-v2",
    originalPrompt: input.originalPrompt,
    preferredLanguage: input.preferredLanguage,
    result: input.result,
  });
  const bytes = new TextEncoder().encode(payload);
  const hash = new Uint8Array(await digest("SHA-256", bytes));
  return [...hash].map((value) => value.toString(16).padStart(2, "0")).join("");
}

export function pendingActionMatchesInput(
  action: PendingAnalysisAction,
  fingerprint: string,
): boolean {
  return Boolean(fingerprint)
    && action.inputFingerprint === fingerprint;
}

export function normalizeAnalysisJobProgress(
  value: unknown,
  status?: string,
): number {
  if (status === "completed") return 100;
  const numeric = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return 0;
  const percent = numeric <= 1 ? numeric * 100 : numeric;
  return Math.max(0, Math.min(100, Math.round(percent)));
}

export function analysisPollDelayMs(attempt: number): number {
  return Math.min(5_000, 1_000 + Math.max(0, attempt) * 500);
}

export function retryAfterSeconds(
  value: string | null | undefined,
  now = Date.now(),
): number | null {
  if (!value) return null;
  const seconds = Number(value);
  if (Number.isFinite(seconds)) {
    return Math.max(1, Math.min(300, Math.ceil(seconds)));
  }
  const date = Date.parse(value);
  if (!Number.isFinite(date)) return null;
  return Math.max(1, Math.min(300, Math.ceil((date - now) / 1_000)));
}

function normalizedFailureText(reason?: string, message?: string): string {
  return `${reason || ""} ${message || ""}`
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/-/g, " ");
}

export function isSafeLegacyAnalysisFallback(
  status: number,
  reason?: string,
  message?: string,
): boolean {
  if ([404, 405, 413, 501].includes(status)) return true;
  const normalized = normalizedFailureText(reason, message);
  if (status === 422) {
    return normalized.includes("invalid analysis job request")
      || normalized.includes("payload too large")
      || normalized.includes("too large to persist");
  }
  if (status !== 503 || normalized.includes("dispatch")) return false;
  return normalized.includes("analysis jobs disabled")
    || (
      normalized.includes("resumable analysis")
      && normalized.includes("unavailable")
    );
}

export function shouldPreserveAnalysisAction(error: unknown): boolean {
  if (isAnalysisJobTerminalFailure(error)) return false;
  if (!(error instanceof AnalysisAPIError)) {
    // A browser/network failure is ambiguous: the create request may have
    // reached the server, so retain the idempotency key and captured input.
    return true;
  }
  if (error.status === 408) return true;
  const normalized = normalizedFailureText(error.reason, error.message);
  if (
    normalized.includes("provider configuration")
    || normalized.includes("provider rejected")
    || normalized.includes("provider quota")
    || normalized.includes("quota exhausted")
    || normalized.includes("invalid api key")
    || normalized.includes("analysis job terminal")
  ) {
    return false;
  }
  if (error.status >= 400 && error.status < 500) return false;
  return true;
}

export function isLockedHoldoutEvaluatingError(
  error: unknown,
): error is AnalysisAPIError {
  return error instanceof AnalysisAPIError
    && error.status === 409
    && normalizedFailureText(error.reason, error.message)
      .includes("locked holdout trial evaluating");
}

export class AnalysisAPIError extends Error {
  readonly status: number;
  readonly reason?: string;
  readonly code?: string;
  readonly localizationKey?: string;
  readonly arguments?: Record<string, string | number | boolean | null>;
  readonly localizedMessage?: string;
  readonly retryable?: boolean;
  readonly retryAfterSeconds?: number;
  readonly marketDataHistoryLimit?: MarketDataHistoryLimit;

  constructor(
    status: number,
    message: string,
    options: {
      reason?: string;
      code?: string;
      localizationKey?: string;
      arguments?: Record<string, string | number | boolean | null>;
      localizedMessage?: string;
      retryable?: boolean;
      retryAfterSeconds?: number | null;
      marketDataHistoryLimit?: MarketDataHistoryLimit;
    } = {},
  ) {
    super(message);
    this.name = "AnalysisAPIError";
    this.status = status;
    this.reason = options.reason;
    this.code = options.code;
    this.localizationKey = options.localizationKey;
    this.arguments = options.arguments;
    this.localizedMessage = options.localizedMessage;
    this.retryable = options.retryable;
    this.retryAfterSeconds = options.retryAfterSeconds ?? undefined;
    this.marketDataHistoryLimit = options.marketDataHistoryLimit;
  }
}

export class AnalysisJobTerminalFailure extends Error {
  readonly reason?: string;
  readonly code?: string;
  readonly localizationKey?: string;
  readonly arguments?: Record<string, string | number | boolean | null>;
  readonly localizedMessage?: string;
  readonly retryable: boolean;

  constructor(job: AnalysisJob) {
    super(job.error?.message || "AI analysis failed.");
    this.name = "AnalysisJobTerminalFailure";
    this.reason = job.error?.reason;
    this.code = job.error?.code;
    this.localizationKey = job.error?.localization_key;
    this.arguments = job.error?.arguments;
    this.localizedMessage = job.error?.localized_message;
    this.retryable = job.error?.retryable ?? true;
  }
}

export function isAnalysisJobTerminalFailure(
  error: unknown,
): error is AnalysisJobTerminalFailure {
  return error instanceof AnalysisJobTerminalFailure;
}

export function savePendingAnalysisAction(
  storage: Storage,
  action: PendingAnalysisAction,
): boolean {
  return writeSession(
    storage,
    analysisActionStorageKey(action.accountID),
    action,
  );
}

export function loadPendingAnalysisAction(
  storage: Storage,
  accountID: string,
  now = Date.now(),
): PendingAnalysisAction | null {
  const key = analysisActionStorageKey(accountID);
  const value = readSession(storage, key);
  const action = validPendingAnalysisAction(value, accountID, now);
  if (!action) {
    removeSession(storage, key);
    return null;
  }
  return action;
}

export function clearPendingAnalysisAction(
  storage: Storage,
  accountID: string,
): void {
  removeSession(storage, analysisActionStorageKey(accountID));
}

export async function savePendingAnalysisActionDurably(
  storage: Storage,
  durableStorage: AsyncWorkflowStorage,
  action: PendingAnalysisAction,
): Promise<boolean> {
  const key = analysisActionStorageKey(action.accountID);
  const encoded = serialized(action);
  if (
    encoded !== null
    && encoded.length <= MAX_INLINE_SESSION_BYTES
    && writeEncodedSession(storage, key, encoded)
  ) {
    await ignoreStorageFailure(() => durableStorage.remove(key));
    return true;
  }
  try {
    await durableStorage.set(key, action);
    writeSession(storage, key, durableMarker(action, "analysis"));
    return true;
  } catch {
    const marker = durableMarker(action, "analysis");
    return marker.resumeAction !== undefined
      && writeSession(storage, key, marker);
  }
}

export async function loadPendingAnalysisActionDurably(
  storage: Storage,
  durableStorage: AsyncWorkflowStorage,
  accountID: string,
  now = Date.now(),
): Promise<PendingAnalysisAction | null> {
  const key = analysisActionStorageKey(accountID);
  const inline = readSession(storage, key);
  const action = validPendingAnalysisAction(inline, accountID, now);
  if (action) return action;
  const marker = validDurableMarker(inline, accountID, "analysis", now)
    ? inline
    : null;
  if (inline !== null && !marker) {
    removeSession(storage, key);
  }
  try {
    const durable = validPendingAnalysisAction(
      await durableStorage.get(key),
      accountID,
      now,
    );
    if (durable) return durable;
    await durableStorage.remove(key);
  } catch {
    // IndexedDB can be unavailable in private/locked-down browser contexts.
  }
  if (marker?.resumeAction) {
    const resume = validPendingAnalysisAction(
      marker.resumeAction,
      accountID,
      now,
    );
    if (resume) return resume;
  }
  if (marker) removeSession(storage, key);
  return null;
}

export async function clearPendingAnalysisActionDurably(
  storage: Storage,
  durableStorage: AsyncWorkflowStorage,
  accountID: string,
): Promise<void> {
  clearPendingAnalysisAction(storage, accountID);
  await ignoreStorageFailure(
    () => durableStorage.remove(analysisActionStorageKey(accountID)),
  );
}

export function savePendingLockedHoldoutAction(
  storage: Storage,
  action: PendingLockedHoldoutAction,
): boolean {
  return writeSession(
    storage,
    lockedHoldoutStorageKey(action.accountID),
    action,
  );
}

export function loadPendingLockedHoldoutAction(
  storage: Storage,
  accountID: string,
  now = Date.now(),
): PendingLockedHoldoutAction | null {
  const key = lockedHoldoutStorageKey(accountID);
  const value = readSession(storage, key);
  const action = validPendingLockedHoldoutAction(value, accountID, now);
  if (!action) {
    removeSession(storage, key);
    return null;
  }
  return action;
}

export function clearPendingLockedHoldoutAction(
  storage: Storage,
  accountID: string,
): void {
  removeSession(storage, lockedHoldoutStorageKey(accountID));
}

export async function savePendingLockedHoldoutActionDurably(
  storage: Storage,
  durableStorage: AsyncWorkflowStorage,
  action: PendingLockedHoldoutAction,
): Promise<boolean> {
  const key = lockedHoldoutStorageKey(action.accountID);
  const encoded = serialized(action);
  if (
    encoded !== null
    && encoded.length <= MAX_INLINE_SESSION_BYTES
    && writeEncodedSession(storage, key, encoded)
  ) {
    await ignoreStorageFailure(() => durableStorage.remove(key));
    return true;
  }
  try {
    await durableStorage.set(key, action);
    writeSession(storage, key, durableMarker(action, "locked_holdout"));
    return true;
  } catch {
    const marker = durableMarker(action, "locked_holdout");
    return marker.resumeAction !== undefined
      && writeSession(storage, key, marker);
  }
}

export async function loadPendingLockedHoldoutActionDurably(
  storage: Storage,
  durableStorage: AsyncWorkflowStorage,
  accountID: string,
  now = Date.now(),
): Promise<PendingLockedHoldoutAction | null> {
  const key = lockedHoldoutStorageKey(accountID);
  const inline = readSession(storage, key);
  const action = validPendingLockedHoldoutAction(inline, accountID, now);
  if (action) return action;
  const marker = validDurableMarker(
    inline,
    accountID,
    "locked_holdout",
    now,
  ) ? inline : null;
  if (inline !== null && !marker) {
    removeSession(storage, key);
  }
  try {
    const durable = validPendingLockedHoldoutAction(
      await durableStorage.get(key),
      accountID,
      now,
    );
    if (durable) return durable;
    await durableStorage.remove(key);
  } catch {
    // IndexedDB can be unavailable in private/locked-down browser contexts.
  }
  if (marker?.resumeAction) {
    const resume = validPendingLockedHoldoutAction(
      marker.resumeAction,
      accountID,
      now,
    );
    if (resume) return resume;
  }
  if (marker) removeSession(storage, key);
  return null;
}

export async function clearPendingLockedHoldoutActionDurably(
  storage: Storage,
  durableStorage: AsyncWorkflowStorage,
  accountID: string,
): Promise<void> {
  clearPendingLockedHoldoutAction(storage, accountID);
  await ignoreStorageFailure(
    () => durableStorage.remove(lockedHoldoutStorageKey(accountID)),
  );
}

function readSession(storage: Storage, key: string): unknown {
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeSession(storage: Storage, key: string, value: unknown): boolean {
  const encoded = serialized(value);
  return encoded !== null && writeEncodedSession(storage, key, encoded);
}

function writeEncodedSession(
  storage: Storage,
  key: string,
  encoded: string,
): boolean {
  try {
    storage.setItem(key, encoded);
    return true;
  } catch {
    return false;
  }
}

function removeSession(storage: Storage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    // Private browsing and locked-down storage should not block the workflow.
  }
}

function sessionIsCurrent(createdAt: string, now: number): boolean {
  const created = Date.parse(createdAt);
  return Number.isFinite(created)
    && created <= now + 60_000
    && now - created <= MAX_SESSION_AGE_MS;
}

function validPendingAnalysisAction(
  value: unknown,
  accountID: string,
  now: number,
): PendingAnalysisAction | null {
  if (
    !isRecord(value)
    || value.version !== 1
    || value.accountID !== accountID
    || typeof value.idempotencyKey !== "string"
    || !value.idempotencyKey
    || typeof value.inputFingerprint !== "string"
    || !/^[a-f0-9]{64}$/.test(value.inputFingerprint)
    || typeof value.createdAt !== "string"
    || !sessionIsCurrent(value.createdAt, now)
    || !isRecord(value.input)
    || typeof value.input.originalPrompt !== "string"
    || typeof value.input.preferredLanguage !== "string"
    || !isBacktestResult(value.input.result)
    || (value.job !== undefined && !isAnalysisJob(value.job))
  ) {
    return null;
  }
  return value as unknown as PendingAnalysisAction;
}

function validPendingLockedHoldoutAction(
  value: unknown,
  accountID: string,
  now: number,
): PendingLockedHoldoutAction | null {
  if (
    !isRecord(value)
    || value.version !== 1
    || value.accountID !== accountID
    || typeof value.trialID !== "string"
    || !value.trialID
    || typeof value.createdAt !== "string"
    || !sessionIsCurrent(value.createdAt, now)
    || !isBacktestResult(value.result)
    || !isRecord(value.analysis)
  ) {
    return null;
  }
  return value as unknown as PendingLockedHoldoutAction;
}

function durableMarker(
  action: PendingAnalysisAction | PendingLockedHoldoutAction,
  kind: DurableSessionMarker["kind"],
): DurableSessionMarker {
  const marker: DurableSessionMarker = {
    version: 1,
    accountID: action.accountID,
    createdAt: action.createdAt,
    durable: true,
    kind,
  };
  if (kind === "analysis" && "inputFingerprint" in action && action.job) {
    marker.resumeAction = {
      ...action,
      input: {
        ...action.input,
        result: compactBacktestResult(action.input.result),
      },
    };
  }
  if (kind === "locked_holdout" && "trialID" in action) {
    marker.resumeAction = {
      ...action,
      result: compactBacktestResult(action.result),
    };
  }
  return marker;
}

function validDurableMarker(
  value: unknown,
  accountID: string,
  kind: DurableSessionMarker["kind"],
  now: number,
): value is DurableSessionMarker {
  return isRecord(value)
    && value.version === 1
    && value.accountID === accountID
    && value.durable === true
    && value.kind === kind
    && typeof value.createdAt === "string"
    && sessionIsCurrent(value.createdAt, now);
}

function compactBacktestResult(result: BacktestResult): BacktestResult {
  return {
    run_id: result.run_id,
    symbol: result.symbol,
    timeframe: result.timeframe,
    strategy_name: result.strategy_name,
    strategy_prompt: result.strategy_prompt,
    generated_at: result.generated_at,
    engine: result.engine,
    candles: [],
    trades: [],
    markers: [],
    performance: result.performance,
    confidence: result.confidence,
    data: result.data
      ? {
        selected_provider: result.data.selected_provider,
        requested_symbol: result.data.requested_symbol,
        interval: result.data.interval,
        candle_count: result.data.candle_count,
        first_candle_time: result.data.first_candle_time,
        last_candle_time: result.data.last_candle_time,
        snapshot_sha256: result.data.snapshot_sha256,
      }
      : null,
    robustness: result.robustness
      ? {
        available: result.robustness.available,
        methodology: result.robustness.methodology,
        out_of_sample_evaluated:
          result.robustness.out_of_sample_evaluated,
      }
      : null,
    usage: result.usage,
    quota_exempt: result.quota_exempt,
    range_adjustment: result.range_adjustment,
  };
}

function serialized(value: unknown): string | null {
  try {
    return JSON.stringify(value);
  } catch {
    return null;
  }
}

function stableJSONString(value: unknown): string {
  return JSON.stringify(canonicalJSONValue(value));
}

function canonicalJSONValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(canonicalJSONValue);
  }
  if (!isRecord(value)) return value;
  return Object.keys(value)
    .sort()
    .reduce<Record<string, unknown>>((result, key) => {
      result[key] = canonicalJSONValue(value[key]);
      return result;
    }, {});
}

async function ignoreStorageFailure(
  operation: () => Promise<void>,
): Promise<void> {
  try {
    await operation();
  } catch {
    // Best-effort cleanup must not turn a completed workflow into an error.
  }
}

function isRecord(value: unknown): value is Record<string, any> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isBacktestResult(value: unknown): value is BacktestResult {
  if (!isRecord(value) || !isRecord(value.performance)) return false;
  return typeof value.symbol === "string"
    && typeof value.timeframe === "string"
    && typeof value.strategy_name === "string"
    && Array.isArray(value.candles);
}

function isAnalysisJob(value: unknown): value is AnalysisJob {
  return isRecord(value)
    && typeof value.id === "string"
    && Boolean(value.id)
    && typeof value.status === "string";
}
